import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  Flex,
  Grid,
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  addDays,
  endOfDay,
  formatDueFriendly,
  isSameDay,
  occurrencesInRange,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "../../utils/todoSchedule";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function monthLabel(date) {
  return date.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function dayKey(date) {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

export default function CalendarView({ todos }) {
  const [mode, setMode] = useState("week");
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));

  const range = useMemo(() => {
    if (mode === "day") {
      return { start: startOfDay(anchor), end: endOfDay(anchor) };
    }
    if (mode === "week") {
      const start = startOfWeek(anchor);
      return { start, end: endOfDay(addDays(start, 6)) };
    }
    const start = startOfMonth(anchor);
    const end = endOfDay(addDays(new Date(start.getFullYear(), start.getMonth() + 1, 0), 0));
    return { start, end };
  }, [anchor, mode]);

  const events = useMemo(() => {
    const rows = [];
    for (const todo of todos) {
      rows.push(...occurrencesInRange(todo, range.start, range.end));
    }
    return rows.sort((a, b) => a.at - b.at);
  }, [todos, range.end, range.start]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const event of events) {
      const key = dayKey(event.at);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(event);
    }
    return map;
  }, [events]);

  const shift = (delta) => {
    if (mode === "day") setAnchor(addDays(anchor, delta));
    else if (mode === "week") setAnchor(addDays(anchor, delta * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1));
  };

  const title =
    mode === "day"
      ? anchor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
      : mode === "week"
        ? `Week of ${startOfWeek(anchor).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
        : monthLabel(anchor);

  return (
    <Stack spacing={4} w="100%">
      <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button isActive={mode === "day"} onClick={() => setMode("day")}>Day</Button>
          <Button isActive={mode === "week"} onClick={() => setMode("week")}>Week</Button>
          <Button isActive={mode === "month"} onClick={() => setMode("month")}>Month</Button>
        </ButtonGroup>
        <HStack>
          <Button size="sm" variant="ghost" onClick={() => shift(-1)} aria-label="Previous">‹</Button>
          <Button size="sm" variant="ghost" onClick={() => setAnchor(startOfDay(new Date()))}>Today</Button>
          <Button size="sm" variant="ghost" onClick={() => shift(1)} aria-label="Next">›</Button>
        </HStack>
      </Flex>

      <Heading size="sm" color="gray.700">{title}</Heading>

      {mode === "month" ? (
        <MonthGrid anchor={anchor} byDay={byDay} selected={anchor} onSelectDay={setAnchor} />
      ) : null}

      {mode === "week" ? (
        <WeekStrip weekStart={startOfWeek(anchor)} byDay={byDay} selected={anchor} onSelectDay={setAnchor} />
      ) : null}

      <DayAgenda
        days={
          mode === "day"
            ? [anchor]
            : mode === "week"
              ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i))
              : [anchor]
        }
        byDay={byDay}
        showDayHeading={mode !== "day"}
      />
    </Stack>
  );
}

function WeekStrip({ weekStart, byDay, selected, onSelectDay }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = startOfDay(new Date());
  return (
    <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={1}>
      {days.map((day) => {
        const key = dayKey(day);
        const count = byDay.get(key)?.length || 0;
        const active = isSameDay(day, selected);
        return (
          <Button
            key={key}
            size="sm"
            variant={active ? "solid" : "ghost"}
            colorScheme={active ? "gray" : undefined}
            flexDirection="column"
            h="auto"
            py={2}
            onClick={() => onSelectDay(day)}
          >
            <Text fontSize="xs">{WEEKDAY_LABELS[day.getDay()]}</Text>
            <Text fontWeight="bold">{day.getDate()}</Text>
            {count ? <Badge mt={1} fontSize="0.6rem">{count}</Badge> : null}
            {isSameDay(day, today) ? <Box w="6px" h="6px" borderRadius="full" bg="blue.400" mt={1} /> : null}
          </Button>
        );
      })}
    </Grid>
  );
}

function MonthGrid({ anchor, byDay, selected, onSelectDay }) {
  const monthStart = startOfMonth(anchor);
  const gridStart = startOfWeek(monthStart);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = startOfDay(new Date());
  return (
    <Box>
      <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={1} mb={1}>
        {WEEKDAY_LABELS.map((label) => (
          <Text key={label} fontSize="xs" textAlign="center" color="gray.500">{label}</Text>
        ))}
      </Grid>
      <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={1}>
        {cells.map((day) => {
          const inMonth = day.getMonth() === anchor.getMonth();
          const key = dayKey(day);
          const count = byDay.get(key)?.length || 0;
          const active = isSameDay(day, selected);
          return (
            <Button
              key={key}
              size="sm"
              variant={active ? "solid" : "ghost"}
              colorScheme={active ? "gray" : undefined}
              opacity={inMonth ? 1 : 0.35}
              h="auto"
              minH="44px"
              py={1}
              onClick={() => onSelectDay(day)}
            >
              <VStack spacing={0}>
                <Text fontSize="sm">{day.getDate()}</Text>
                {count ? <Box w="6px" h="6px" borderRadius="full" bg="teal.400" /> : null}
                {isSameDay(day, today) ? <Box w="full" h="2px" bg="blue.400" borderRadius="full" /> : null}
              </VStack>
            </Button>
          );
        })}
      </Grid>
    </Box>
  );
}

function DayAgenda({ days, byDay, showDayHeading }) {
  const today = startOfDay(new Date());
  return (
    <Stack spacing={4}>
      {days.map((day) => {
        const key = dayKey(day);
        const items = byDay.get(key) || [];
        if (!items.length && showDayHeading && !isSameDay(day, today)) return null;
        return (
          <Box key={key}>
            {showDayHeading ? (
              <Text fontWeight="semibold" fontSize="sm" mb={2} color="gray.700">
                {day.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
              </Text>
            ) : null}
            {items.length ? (
              <Stack spacing={2}>
                {items.map((event, index) => (
                  <Box key={`${event.todo.id}-${index}`} bg="white" borderRadius="lg" px={3} py={2} shadow="sm">
                    <HStack justify="space-between" align="start">
                      <Text fontWeight="medium" fontSize="sm" flex={1}>{event.todo.task}</Text>
                      <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                        {formatDueFriendly(event.at)}
                      </Text>
                    </HStack>
                    {event.repeating ? <Badge size="sm" mt={1}>Repeats</Badge> : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Text fontSize="sm" color="gray.500">Nothing scheduled</Text>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
